import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { formatDateOnlyLocal } from '@erp/shared-utils';
import {
  MarkAPInvoicePaidMethodSchema,
  type MarkAPInvoicePaidMethod,
  type MarkAPInvoicesPaidInput,
} from '@erp/rpc';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

function isPaidOutsideMethod(
  value: string | null,
): value is MarkAPInvoicePaidMethod {
  return (
    value != null &&
    (MarkAPInvoicePaidMethodSchema.options as readonly string[]).includes(value)
  );
}

export function MarkInvoicesPaidDialog({
  open,
  onClose,
  invoiceIds,
  loading,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  invoiceIds: string[];
  loading: boolean;
  onConfirm: (payload: MarkAPInvoicesPaidInput) => void;
}) {
  const gt = useGT();
  const [paidDate, setPaidDate] = useState<Date | undefined>(new Date());
  const [method, setMethod] = useState<MarkAPInvoicePaidMethod>('other');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [note, setNote] = useState('');

  const count = invoiceIds.length;
  const title =
    count === 1
      ? gt('Mark as Paid', {
          context: 'dialog title, record one AP invoice paid outside Gently',
        })
      : gt('Mark {count} as Paid', {
          context:
            'dialog title, record several AP invoices paid outside Gently',
          count: String(count),
        });
  const message =
    count === 1
      ? gt(
          'Record that this invoice was paid outside Gently. Gently will not send a payment.',
          {
            context: 'dialog body, mark one AP invoice paid outside Gently',
          },
        )
      : gt(
          'Record that these {count} invoices were paid outside Gently. Gently will not send a payment.',
          {
            context:
              'dialog body, mark several AP invoices paid outside Gently',
            count: String(count),
          },
        );

  function resetForm() {
    setPaidDate(new Date());
    setMethod('other');
    setReferenceNumber('');
    setNote('');
  }

  function handleClose() {
    if (loading) return;
    resetForm();
    onClose();
  }

  function handleConfirm() {
    if (!paidDate || invoiceIds.length === 0) return;
    onConfirm({
      invoiceIds,
      paidDate: formatDateOnlyLocal(paidDate),
      method,
      referenceNumber: referenceNumber.trim() || undefined,
      note: note.trim() || undefined,
    });
  }

  return (
    <ConfirmDialog
      open={open}
      onClose={handleClose}
      onConfirm={handleConfirm}
      closeOnConfirm={false}
      loading={loading}
      disabled={!paidDate || invoiceIds.length === 0}
      title={title}
      message={message}
      confirmText={gt('Mark as Paid', {
        context: 'confirm button, record AP invoices paid outside Gently',
      })}
      cancelText={gt('Cancel', {
        context: 'cancel button, mark AP invoices paid',
      })}
      className="max-w-lg"
    >
      <div className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="mark-paid-date">
            <T context="field label, date the supplier was paid outside Gently">
              Paid Date
            </T>
          </Label>
          <DatePicker
            id="mark-paid-date"
            value={paidDate}
            onChange={setPaidDate}
            toDate={new Date()}
            placeholder={gt('Select date', {
              context: 'date picker placeholder, paid date',
            })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="mark-paid-method">
            <T context="field label, how the invoice was paid outside Gently">
              Method
            </T>
          </Label>
          <Select
            value={method}
            onValueChange={(next) => {
              if (isPaidOutsideMethod(next)) setMethod(next);
            }}
          >
            <SelectTrigger id="mark-paid-method">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ach">
                <T context="payment method, bank transfer">ACH</T>
              </SelectItem>
              <SelectItem value="wire">
                <T context="payment method, wire transfer">Wire</T>
              </SelectItem>
              <SelectItem value="check">
                <T context="payment method, paper check">Check</T>
              </SelectItem>
              <SelectItem value="card">
                <T context="payment method, card">Card</T>
              </SelectItem>
              <SelectItem value="cash">
                <T context="payment method, cash">Cash</T>
              </SelectItem>
              <SelectItem value="other">
                <T context="payment method, something else">Other</T>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="mark-paid-reference">
            <T context="field label, optional check number or bank confirmation">
              Reference
            </T>
          </Label>
          <Input
            id="mark-paid-reference"
            value={referenceNumber}
            maxLength={100}
            placeholder={gt('Check number or confirmation', {
              context: 'placeholder, optional payment reference',
            })}
            onChange={(event) => setReferenceNumber(event.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="mark-paid-note">
            <T context="field label, optional note when marking an invoice paid">
              Note
            </T>
          </Label>
          <Textarea
            id="mark-paid-note"
            value={note}
            maxLength={500}
            placeholder={gt('Optional note', {
              context:
                'placeholder, optional note when marking an invoice paid',
            })}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>
      </div>
    </ConfirmDialog>
  );
}
